import React, { useEffect, useRef, useState } from 'react';
import './HomeMeetingOwlPro.scss';
import CTAButton from '../../UI/CTA/CTAButton/CTAButton';
import { BREAKPOINTS } from '../../../constants/breakpoints';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
gsap.registerPlugin(ScrollTrigger);

const DESKTOP_WIDTH = BREAKPOINTS.large;

/**
 * Renders homepage meeting owl pro module
 * @param {string} title
 * @param {string} description
 * @param {string} ctaText
 * @param {string} ctaLink
 * @param {object} imageLeft
 * @param {object} imageRight
 * @param {string} backgroundImage
 * @returns {JSX.Element}
 * @constructor
 */
const HomeMeetingOwlPro = ({
  title,
  description,
  ctaText,
  ctaLink,
  imageLeft,
  imageRight,
  backgroundImage,
}) => {
  const [isDesktop, setIsDesktop] = useState(window.innerWidth > DESKTOP_WIDTH);
  const imageLeftElement = useRef();
  const imageRightElement = useRef();
  const container = useRef();

  const IMG_LEFT_END_WIDTH_VALUE = isDesktop ? '18rem' : '8.375rem';
  const IMG_RIGHT_END_WIDTH_VALUE = isDesktop ? '24rem' : '11.125rem';
  const IMG_LEFT_START_WIDTH_VALUE = isDesktop ? '44rem' : '19.5rem';

  useEffect(() => {
    window.addEventListener('resize', handleResize);

    const imagesTween = gsap.timeline({
      scrollTrigger: {
        trigger: container.current,
        start: `top center${isDesktop ? '+=200px' : '-=200px'}`,
        end: 'bottom bottom',
        toggleActions: 'play none none reverse',
        scrub: true,
        invalidateOnRefresh: true,
      },
    });

    imagesTween.fromTo(
      imageLeftElement.current,
      {
        width: IMG_LEFT_START_WIDTH_VALUE,
      },
      {
        width: IMG_LEFT_END_WIDTH_VALUE,
      },
      0,
    );

    imagesTween.fromTo(
      imageRightElement.current,
      {
        width: '0',
      },
      {
        width: IMG_RIGHT_END_WIDTH_VALUE,
      },
      0,
    );

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleResize = () => {
    setIsDesktop(window.innerWidth > DESKTOP_WIDTH);
  };

  const extendedStyles = {
    backgroundImage: `url(${backgroundImage})`,
    backgroundSize: isDesktop ? '300%' : '800%',
    backgroundPosition: `left 65% bottom ${isDesktop ? '32%' : '30%'}`,
    backgroundRepeat: 'no-repeat',
    transform: 'translate3d(0,0,0)',
  };

  return (
    <div
      ref={container}
      className="c-home-meeting-owl-pro module-background-color-orange"
      style={extendedStyles}
    >
      <div className="c-home-meeting-owl-pro__inner">
        <div className="c-home-meeting-owl-pro__text">
          <div className="c-home-meeting-owl-pro__title h3-right-grotesk">
            <h3>{title}</h3>
          </div>
          <div className="c-home-meeting-owl-pro__description h3-roobert">
            <h3>{description}</h3>
          </div>
          <div className="c-home-meeting-owl-pro__cta">
            {ctaText !== '' && (
              <CTAButton
                type={'primary'}
                link={ctaLink}
                text={ctaText}
                target={'_self'}
                additionalClasses={'cta'}
              />
            )}
          </div>
        </div>
        <div className="c-home-meeting-owl-pro__images-container">
          <div ref={imageLeftElement} className="c-home-meeting-owl-pro__image-left">
            <img loading="lazy" src={imageLeft.url} alt={imageLeft.alt} />
          </div>
          <div ref={imageRightElement} className="c-home-meeting-owl-pro__image-right">
            <img loading="lazy" src={imageRight.url} alt={imageRight.alt} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default HomeMeetingOwlPro;
